import _ from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { useRouteMatch } from 'react-router-dom/cjs/react-router-dom.min';
import {
  AccountingIntegrationIcon,
  ActionsBanner,
  Currency,
  DateTime,
  ExpenseLockIcon,
  ExportDialog,
  ExportDropdown,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  ListViewSelection,
  Page,
  PeriodFilter,
  YesNoFilter,
} from '~/components';
import { BulkActionMenu } from '~/components/bulk-action';
import {
  ClientFiltersBar,
  ClientFiltersGroup,
  ExpenseFiltersBar,
  ExpenseFiltersGroup,
  FilterButton,
  FiltersDrawer,
  MemberFiltersBar,
  MemberFiltersGroup,
  ProjectFiltersBar,
  ProjectFiltersGroup,
} from '~/components/filters';
import { useApi, useConfirmation, useIntegrations, useWorkspace } from '~/contexts';
import {
  useAuth,
  useClientFilters,
  useDocumentTitle,
  useExpenseFilters,
  useIsMounted,
  useMemberFilters,
  useProjectFilters,
  useRowSelection,
  useSearchParams,
  useSearchParamsConfig,
} from '~/hooks';
import { PageLoader } from '~/routes/public/pages';
import { QuerySort, intervalOptions, intervalsByScope, mimeTypes } from '~/utils';
import ExpenseAccountingIndicator from '../ExpenseAccountingIndicator';
import DeleteExpenseItemConfirmation from '../DeleteExpenseItemConfirmation';
import ExpenseAmountWidget from '../ExpenseAmountWidget';
import expenseLockedReasons from '../expenseLockedReason';
import ExpenseItemDrawer from '../item/ExpenseItemDrawer';
import BulkDelete from './bulk/BulkDelete';
import BulkSetApprovalStatus from './bulk/BulkSetApprovalStatus';
import BulkSetReimbursed from './bulk/BulkSetReimbursed';

function ExpenseAuditingPage({ parentUrl }) {
  const documentTitle = useDocumentTitle('Expense Auditing');

  const { expenseItemId, mode } = useParams();
  const api = useApi();
  const confirmation = useConfirmation();
  const history = useHistory();
  const location = useLocation();
  const { url } = useRouteMatch();
  const { workspace } = useWorkspace();
  const auth = useAuth();
  const integrations = useIntegrations();

  const memberFilters = useMemberFilters();
  const clientFilters = useClientFilters();
  const projectFilters = useProjectFilters();
  const expenseFilters = useExpenseFilters();

  const [query, setQuery] = useState({
    isReady: false,
    searchParamsStatus: 'pending',
    data: null,
    params: {
      period: null,
      sort: new QuerySort('date', 'desc'),
      page: 0,
      size: 100,
      includeLockedItems: 'yes',
      imported: null,
      excludedFromBilling: null,
      ...memberFilters.filters,
      ...clientFilters.filters,
      ...projectFilters.filters,
      ...expenseFilters.filters,
    },
    action: 'load',
  });

  const setParams = (params) => {
    setQuery((state) => ({
      ...state,
      action: 'filter',
      params: { ...state.params, ...params, page: 0 },
      searchParamsStatus: 'ready',
    }));
  };

  const loadMore = () => {
    setQuery((state) => {
      if (
        state.searchParamsStatus !== 'ready' ||
        state.action !== null ||
        !state.data ||
        state.data.total <= state.data.results.length
      )
        return state;

      return {
        ...state,
        params: { ...state.params, page: state.params.page + 1 },
        action: 'load-more',
      };
    });
  };

  const { action, data, isReady, params, searchParamsStatus } = query;

  const selection = useRowSelection({ rows: data?.results });

  const searchParamsConfig = useSearchParamsConfig();

  const searchParams = useSearchParams({
    config: useMemo(
      () => ({
        period: { ...searchParamsConfig.period, default: intervalOptions.past_90_days },
        sort: { default: new QuerySort('date', 'desc'), ...searchParamsConfig.sort },
        includeLockedItems: { default: 'yes', valid: ['yes', 'no'] },
        imported: { valid: ['yes', 'no'] },
        excludedFromBilling: { valid: ['yes', 'no'] },
        ...memberFilters.searchParamsConfig,
        ...clientFilters.searchParamsConfig,
        ...projectFilters.searchParamsConfig,
        ...expenseFilters.searchParamsConfig,
      }),
      [searchParamsConfig, memberFilters, clientFilters, projectFilters, expenseFilters],
    ),
    sessionKey: 'expense_auditing',
    onChange: useCallback((params) => setParams((state) => ({ ...state, ...params })), []),
  });

  useEffect(() => {
    if (searchParamsStatus !== 'pending') return;
    searchParams.get().then((params) => {
      if (params) setParams(params);
    });
  }, [searchParams, searchParamsStatus]);

  const urlSearchParams = useMemo(
    () => ({
      start: params.period?.start ?? undefined,
      end: params.period?.end ?? undefined,
      sort: params.sort,
      includeLockedItems: params.includeLockedItems ?? undefined,
      imported: params.imported ?? undefined,
      excludeFromBilling: params.excludeFromBilling ?? undefined,
      ...memberFilters.mapUrlSearchParams(params),
      ...clientFilters.mapUrlSearchParams(params),
      ...projectFilters.mapUrlSearchParams(params),
      ...expenseFilters.mapUrlSearchParams(params),
    }),
    [params, memberFilters, clientFilters, projectFilters, expenseFilters],
  );

  const isMounted = useIsMounted();

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .expenseAdmin()
        .auditing({ ...urlSearchParams, page: params.page, size: params.size });
      if (!isMounted.current) return;
      setQuery((state) => ({
        ...state,
        action: null,
        searchParamsStatus: 'ready',
        data: {
          ...data,
          results: state.action === 'load-more' ? [...state.data.results, ...data.results] : data.results,
          total: data.total,
        },
      }));
    } catch (error) {
      setQuery((state) => ({ ...state, data: { total: 0, results: [] } }));
    }
  }, [workspace.id, urlSearchParams, api, isMounted, params.page, params.size]);

  useEffect(() => {
    if (searchParamsStatus !== 'ready') return;
    fetchData();
  }, [fetchData, searchParamsStatus]);

  const handleFilter = (values) => {
    if (values !== params) {
      setQuery((state) => ({ ...state, status: 'filtering' }));
      selection.clear();
    }

    setParams({ ...values });
    searchParams.set({ ..._.omit(values, 'sort') });
    hideFilters();
  };

  const load = useCallback(() => {
    setQuery((state) => ({ ...state, action: 'load', params: { ...state.params, page: 0 } }));
  }, []);

  const handleSort = ({ column, sort }) => {
    const direction = column === sort.column && sort.direction === 'asc' ? 'desc' : 'asc';
    const querySort = new QuerySort(column, direction);
    setParams({ sort: querySort });
    searchParams.set({ sort: querySort });
    selection.clear();
  };

  const handleDeleted = () => {
    selection.clear();
    load();
  };

  async function handleReimbursedStatusChange(item, flag) {
    await api.www.workspaces(workspace.id).expenseAdmin(item.id).setReimbursedStatus(flag);
    selection.clear();
    load();
  }

  const [filtersVisible, setFiltersVisible] = useState(false);
  const showFilters = () => setFiltersVisible(true);
  const hideFilters = () => setFiltersVisible(false);

  if (!isReady && !data) return <PageLoader />;

  const handleExport = async (filename, mimeType) => {
    await confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onLoad={api.www
          .workspaces(workspace.id)
          .expenseAdmin()
          .exportAuditing(
            { ...urlSearchParams, size: null },
            {
              headers: { accept: mimeType },
              responseType: 'blob',
            },
          )}
        onClose={resolve}
      />
    ));
  };

  const handleDelete = async (id) => {
    await confirmation.prompt((resolve) => (
      <DeleteExpenseItemConfirmation
        id={id}
        onClose={() => {
          resolve();
        }}
        onDelete={() => {
          handleDeleted();
          resolve();
        }}
      />
    ));
  };

  const handleBulkDelete = async () => {
    await confirmation.prompt((resolve) => (
      <BulkDelete
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetApprovalStatus = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetApprovalStatus
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  const handleBulkSetReimbursedStatus = async () => {
    await confirmation.prompt((resolve) => (
      <BulkSetReimbursed
        ids={selection.ids}
        onComplete={async () => {
          selection.clear();
          if (params.page !== 0) {
            await fetchData();
          } else {
            setParams({ page: 0 });
          }
        }}
        onClose={resolve}
      />
    ));
  };

  return (
    <>
      <Page>
        <Page.Header>
          <Page.Info>
            <Page.Eyebrow>Expenses</Page.Eyebrow>
            <Page.Title>Expense Auditing</Page.Title>
          </Page.Info>

          <Page.Actions>
            <ExportDropdown>
              {({ setIsOpen }) => (
                <>
                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`expense_auditing.csv`, mimeTypes.csv);
                      setIsOpen(false);
                    }}>
                    Export to CSV
                  </ExportDropdown.Item>

                  <ExportDropdown.Item
                    onClick={async () => {
                      await handleExport(`expense_auditing.xlsx`, mimeTypes.xlsx);
                      setIsOpen(false);
                    }}>
                    Export to Excel
                  </ExportDropdown.Item>
                </>
              )}
            </ExportDropdown>

            <FilterButton isOutline onClick={showFilters} />
          </Page.Actions>
        </Page.Header>

        <Page.Filters>
          <FiltersBar>
            <PeriodFilter
              name="period"
              placeholder="Date Range"
              value={params.period}
              intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
              onChange={({ value }) => handleFilter({ period: value })}
            />

            {!_.isEmpty(params.includeLockedItems) && (
              <LockStatusFilter
                value={params.includeLockedItems}
                onChange={({ value }) => handleFilter({ includeLockedItems: value })}
              />
            )}

            {!_.isEmpty(params.imported) && (
              <ImportedFilter value={params.imported} onChange={({ value }) => handleFilter({ imported: value })} />
            )}

            {!_.isEmpty(params.excludeFromBilling) && (
              <ExcludeFromBillingFilter
                value={params.excludeFromBilling}
                onChange={({ value }) => handleFilter({ excludeFromBilling: value })}
              />
            )}

            <ClientFiltersBar filters={params} onChange={handleFilter} />

            <ProjectFiltersBar filters={params} sticky={['projects']} onChange={handleFilter} />

            <ExpenseFiltersBar
              filters={params}
              sticky={[
                'expenseApprovalStatuses',
                'expenseCategories',
                'expenseReimbursableTypeId',
                'expenseIsReimbursed',
              ]}
              onChange={handleFilter}
            />

            <MemberFiltersBar filters={params} sticky={['members', 'memberPractices']} onChange={handleFilter} />
          </FiltersBar>
        </Page.Filters>

        <Page.ListView>
          <ListView>
            <ListView.Header>
              <ListViewSelection.Column
                selection={selection}
                disabled={data.results.length === 0 || action === 'filter'}
              />
              <ListView.Column name="selector" width="3.5rem" align="center">
                <ExpenseLockIcon />
              </ListView.Column>
              <ListView.Column width="3rem" isVisible={auth.workspace.manage && integrations.accounting}>
                <AccountingIntegrationIcon />
              </ListView.Column>
              <ListView.Column name="date" width="7.5rem" sort={params.sort} onSort={handleSort}>
                Date
              </ListView.Column>
              <ListView.Column name="member" minWidth="12rem" sort={params.sort} onSort={handleSort}>
                Member
              </ListView.Column>
              <ListView.Column name="report" minWidth="16rem" sort={params.sort} onSort={handleSort}>
                Expense Report
              </ListView.Column>
              <ListView.Column name="category" minWidth="16rem" sort={params.sort} onSort={handleSort}>
                Category/Vendor
              </ListView.Column>
              <ListView.Column name="project" width="20rem" sort={params.sort} onSort={handleSort}>
                Project/Client
              </ListView.Column>
              <ListView.Column name="amount" width="12.5rem" align="right" sort={params.sort} onSort={handleSort}>
                Amount
              </ListView.Column>
              <ListView.Column name="selector" width="3rem"></ListView.Column>
            </ListView.Header>
            <ListView.Body fade={action === 'filter'}>
              {_.map(data.results, (item) => {
                const {
                  id,
                  vendorName,
                  lockStatusId,
                  expenseCategory,
                  unitAmount,
                  unitCount,
                  permissions,
                  project,
                  member,
                  date,
                  report,
                } = item;

                const isLocked = lockStatusId !== 'unlocked';

                const handleOpenItem = (item, readOnly = false) => {
                  history.push({
                    pathname: readOnly || isLocked ? `${url}/item/${item.id}/view` : `${url}/item/${item.id}/edit`,
                    search: location.search,
                    state: { scrollToTop: false },
                  });
                };

                return (
                  <ListView.Row key={id} onClick={() => handleOpenItem(item)}>
                    <ListViewSelection.Cell id={item.id} selection={selection} />
                    <ListView.Cell>
                      {isLocked && (
                        <ExpenseLockIcon
                          value={lockStatusId}
                          message={expenseLockedReasons[lockStatusId]}
                          style={{ fontSize: '0.875rem' }}
                        />
                      )}
                    </ListView.Cell>
                    <ListView.Cell>
                      <ExpenseAccountingIndicator
                        accountingProvider={item.accountingProvider ?? item.invoice?.accountingProvider}
                        accountingConnectionName={
                          item.accountingConnectionName ?? item.invoice?.accountingConnectionName
                        }
                        invoiceNumber={item.invoice?.accountingSynced ? item.invoice.number : null}
                        billNumber={item.accountingBillNumber}
                        isSynced={!!item.accountingProvider}
                      />
                    </ListView.Cell>
                    <ListView.Cell>
                      <DateTime value={date} />
                    </ListView.Cell>
                    <ListView.Cell>{member?.name}</ListView.Cell>
                    <ListView.Cell>
                      <ListView.CellContent>
                        {report.number}
                        <small>{report.title}</small>
                      </ListView.CellContent>
                    </ListView.Cell>
                    <ListView.Cell>
                      <ListView.CellContent>
                        {expenseCategory.name}
                        {vendorName && <small>{vendorName}</small>}
                        {expenseCategory.unitName && (
                          <small>
                            {unitCount} x <Currency value={unitAmount} maximumFractionDigits={4} /> per{' '}
                            {expenseCategory.unitName}
                          </small>
                        )}
                      </ListView.CellContent>
                    </ListView.Cell>
                    <ListView.Cell>
                      {project && (
                        <ListView.CellContent>
                          {project.name}
                          <small>{project.client.name}</small>
                        </ListView.CellContent>
                      )}
                    </ListView.Cell>
                    <ListView.Cell>
                      <ExpenseAmountWidget item={item} />
                    </ListView.Cell>
                    <ListViewActions>
                      {isLocked ? (
                        <ListViewActions.View onClick={() => handleOpenItem(item, true)} />
                      ) : (
                        <ListViewActions.Edit onClick={() => handleOpenItem(item)} />
                      )}

                      <hr />
                      <ListViewMenu>
                        {({ setIsOpen }) => {
                          const handleAction = (action) => setIsOpen(false) || action;

                          return (
                            <>
                              <ListViewMenu.Item onClick={() => handleOpenItem(item, true)}>View</ListViewMenu.Item>
                              <ListViewMenu.Item
                                disabled={isLocked}
                                tooltip={isLocked ? expenseLockedReasons[lockStatusId] : undefined}
                                onClick={() => handleOpenItem(item)}>
                                Edit
                              </ListViewMenu.Item>
                              <ListViewMenu.Item
                                disabled={isLocked}
                                tooltip={isLocked ? expenseLockedReasons[lockStatusId] : undefined}
                                onClick={() => handleAction(handleDelete(item.id))}>
                                Delete
                              </ListViewMenu.Item>
                              <ListViewMenu.Item
                                disabled={!permissions.reimburse || !item.isReimbursable}
                                tooltip={
                                  !permissions.reimburse
                                    ? 'You do not have administrative privileges to set the reimbursed status.'
                                    : !item.isReimbursable
                                      ? 'Expense item is not set as reimbursable.'
                                      : undefined
                                }
                                onClick={() => handleAction(handleReimbursedStatusChange(item, !item.isReimbursed))}>
                                {item.isReimbursed ? 'Set as Not Reimbursed' : 'Set as Reimbursed'}
                              </ListViewMenu.Item>
                            </>
                          );
                        }}
                      </ListViewMenu>
                    </ListViewActions>
                  </ListView.Row>
                );
              })}

              {data.results.length === 0 && <ListView.Empty />}

              {data.total > data.results.length && <ListView.Loader onIntersecting={loadMore} />}
            </ListView.Body>

            <ListView.Status total={data.total} label="Expense Item" loading={!!action} />
          </ListView>
        </Page.ListView>

        {selection.any && (
          <ActionsBanner>
            <BulkActionMenu count={selection.count}>
              {auth.expenses.reimburse && (
                <BulkActionMenu.Item onClick={() => handleBulkSetReimbursedStatus()}>
                  Set reimbursed status
                </BulkActionMenu.Item>
              )}
              {auth.expenses.approve && (
                <BulkActionMenu.Item onClick={() => handleBulkSetApprovalStatus()}>
                  Set approval status
                </BulkActionMenu.Item>
              )}
              <BulkActionMenu.Item onClick={() => handleBulkDelete()}>Delete</BulkActionMenu.Item>
            </BulkActionMenu>
          </ActionsBanner>
        )}
      </Page>

      <Filters values={params} isOpen={filtersVisible} onApply={handleFilter} onClose={hideFilters} />

      {expenseItemId && (
        <ExpenseItemDrawer
          overrideShowMember={true}
          mode={mode}
          onSaved={fetchData}
          onClose={() => {
            history.push({ pathname: parentUrl, search: location.search, state: { scrollToTop: false } });
            documentTitle.set('Expense Auditing');
          }}
        />
      )}
    </>
  );
}

function Filters({ values, isOpen, onClose, onApply }) {
  const auth = useAuth();

  const [filters, setFilters] = useState(values);

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  const handleFilter = (filter) => {
    setFilters({ ...filters, ...filter });
  };

  const handleCancel = () => {
    setFilters(values);
    onClose();
  };

  useEffect(() => {
    setFilters(values);
  }, [values]);

  return (
    <FiltersDrawer isOpen={isOpen} onApply={handleApply} onClose={handleCancel}>
      <PeriodFilter
        name="period"
        placeholder="Date Range"
        value={filters.period}
        intervals={[intervalOptions.all_dates, ...intervalsByScope.day]}
        onChange={({ value }) => handleFilter({ period: value })}
      />

      <LockStatusFilter
        value={filters.includeLockedItems}
        onChange={({ value }) => handleFilter({ includeLockedItems: value })}
      />

      {auth.workspace.manage && (
        <ImportedFilter value={filters.imported} onChange={({ value }) => handleFilter({ imported: value })} />
      )}

      {auth.workspace.manage && (
        <ExcludeFromBillingFilter
          value={filters.excludeFromBilling}
          onChange={({ value }) => handleFilter({ excludeFromBilling: value })}
        />
      )}

      <ClientFiltersGroup filters={filters} onChange={handleFilter} />

      <ProjectFiltersGroup filters={filters} onChange={handleFilter} />

      <MemberFiltersGroup filters={filters} onChange={handleFilter} />

      <ExpenseFiltersGroup filters={filters} onChange={handleFilter} />
    </FiltersDrawer>
  );
}

function LockStatusFilter({ value, onChange }) {
  return <YesNoFilter name="includeLockedItems" placeholder="Include Locked Items" value={value} onChange={onChange} />;
}

function ImportedFilter({ value, onChange }) {
  return <YesNoFilter name="imported" placeholder="Imported" value={value} clearable onChange={onChange} />;
}

function ExcludeFromBillingFilter({ value, onChange }) {
  return (
    <YesNoFilter
      name="excludeFromBilling"
      placeholder="Exclude from Billing"
      value={value}
      clearable
      onChange={onChange}
    />
  );
}

export default ExpenseAuditingPage;
